import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import TwoFactorAuth from './components/auth/TwoFactorAuth';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/Dashboard';
import PrescriptionUpload from './components/PrescriptionUpload';
// EDIT: This import is no longer needed here as it's handled by AdminDashboard
// import PrescriptionDetail from './components/PrescriptionDetail'; 
import Profile from './components/Profile';
import AdminDashboard from './components/admin/AdminDashboard';
import UpdateApp from './components/UpdateApp';
import './App.css';

// Protected Route Component (No changes needed here)
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Main App Component
const AppContent = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <Routes>
        {/* Public Routes (No changes here) */}
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
        />
        <Route 
          path="/signup" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Signup />} 
        />
        <Route 
          path="/2fa" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <TwoFactorAuth />} 
        />
        <Route 
          path="/forgot-password" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} 
        />
        <Route 
          path="/reset-password" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPassword />} 
        />
        <Route 
          path="/update" 
          element={<UpdateApp />} 
        />
        
        {/* Protected Routes */}
        <Route 
          path="/dashboard/*" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/upload" 
          element={
            <ProtectedRoute>
              <PrescriptionUpload />
            </ProtectedRoute>
          } 
        />
        
        {/* EDIT: The /prescription/:id route has been REMOVED from here.
            It will now be handled as a nested route within AdminDashboard. */}
        
        {/* EDIT: The path for AdminDashboard has been changed from "/admin" to "/admin/*"
            This allows it to match all nested routes like /admin/upload, /admin/users, etc. */}
        <Route 
          path="/admin/*" 
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        
        {/* Default Route (No changes here) */}
        <Route 
          path="/" 
          element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} 
        />
        <Route 
          path="*" 
          element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} 
        />
      </Routes>
      
      {/* Toaster (No changes needed here) */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000, 
          style: {
            background: 'linear-gradient(135deg, #2563eb 0%, #0d9488 100%)',
            color: '#fff',
            borderRadius: '16px',
            boxShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
          success: {
            duration: 3000,
            style: {
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.3)',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 10px 25px -5px rgba(239, 68, 68, 0.3)',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#ef4444',
            },
          },
        }}
      />
    </div>
  );
};

// App Component with Providers (No changes needed here)
const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;